import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { retry } from 'rxjs/operators';
import { environment } from '@environments/environment';

/**
 * REST client for the JMotto10 Arrivals Agent endpoints
 * (com.jmotto.multi.arrivals.ArrivalsController).
 */
@Injectable({ providedIn: 'root' })
export class ArrivalsAgentService {

	private base = `${environment.apiUrl}/arrivals`;

	// Stage codes — must match com.jmotto.multi.arrivals.ArrivalStageEnum
	public static readonly STAGE_WELCOME_MORNING       = 1;
	public static readonly STAGE_HOUSEKEEPING_STARTED  = 2;
	public static readonly STAGE_INSPECTION            = 3;
	public static readonly STAGE_READY_FOR_INSPECTION  = 4;
	public static readonly STAGE_READY_FOR_CHECKIN     = 5;
	public static readonly STAGE_CHECKED_IN_THANKS     = 6;
	public static readonly STAGE_WEEK_BEFORE_ARRIVAL   = 7;
	public static readonly STAGE_DAY_BEFORE_ARRIVAL    = 8;
	public static readonly STAGE_DAY_AFTER_ARRIVAL     = 9;
	public static readonly STAGE_DAY_BEFORE_DEPARTURE  = 10;
	public static readonly STAGE_DAY_OF_DEPARTURE      = 11;
	public static readonly STAGE_DAY_AFTER_DEPARTURE   = 12;

	// Ordered chronologically left-to-right for the lifecycle dot strip.
	public static readonly STAGES: Array<{
		code: number;
		key: string;
		label: string;
		short: string;
		group: 'pre' | 'arrival' | 'in-stay' | 'departure' | 'post';
		triggerType: 'TIME' | 'STATE' | 'DRIP';
	}> = [
		{ code:  7, key: 'WEEK_BEFORE_ARRIVAL',  label: 'One Week Before Arrival',      short: '-7D',  group: 'pre',       triggerType: 'TIME'  },
		{ code:  8, key: 'DAY_BEFORE_ARRIVAL',   label: 'Day Before Arrival',           short: '-1D',  group: 'pre',       triggerType: 'TIME'  },
		{ code:  1, key: 'WELCOME_MORNING',      label: 'Welcome (Morning)',            short: 'AM',   group: 'arrival',   triggerType: 'TIME'  },
		{ code:  2, key: 'HOUSEKEEPING_STARTED', label: 'Housekeeping Started',         short: 'HK',   group: 'arrival',   triggerType: 'STATE' },
		{ code:  4, key: 'READY_FOR_INSPECTION', label: 'Ready for Inspection',         short: 'RI',   group: 'arrival',   triggerType: 'STATE' },
		{ code:  3, key: 'INSPECTION',           label: 'Inspection',                   short: 'IN',   group: 'arrival',   triggerType: 'STATE' },
		{ code:  5, key: 'READY_FOR_CHECKIN',    label: 'Ready for Check-In',           short: 'CI',   group: 'arrival',   triggerType: 'STATE' },
		{ code:  6, key: 'CHECKED_IN_THANKS',    label: 'Checked-In Thanks',            short: 'TX',   group: 'arrival',   triggerType: 'STATE' },
		{ code:  9, key: 'DAY_AFTER_ARRIVAL',    label: 'Day After Arrival',            short: '+1D',  group: 'in-stay',   triggerType: 'TIME'  },
		{ code: 10, key: 'DAY_BEFORE_DEPARTURE', label: 'Day Before Departure',         short: 'D-1',  group: 'departure', triggerType: 'TIME'  },
		{ code: 11, key: 'DAY_OF_DEPARTURE',     label: 'Day of Departure',             short: 'DEP',  group: 'departure', triggerType: 'TIME'  },
		{ code: 12, key: 'DAY_AFTER_DEPARTURE',  label: 'Day After Departure (Review)', short: 'REV',  group: 'post',      triggerType: 'DRIP'  },
	];

	// MsgVO.status codes
	public static readonly MSG_STATUS_DRAFT  = 1;
	public static readonly MSG_STATUS_SENT   = 2;
	public static readonly MSG_STATUS_FAILED = 4;
	public static readonly MSG_STATUS_OUTBOX = 5;

	// Mirror of RoomStatusEnum
	public static readonly ROOM_STATUSES: { [code: number]: { name: string; color: string } } = {
		1: { name: 'Occupied',             color: '#e6b800'  },
		2: { name: 'Dirty',               color: '#8b0000'  },
		3: { name: 'Housekeeping',         color: '#e91e8c'  },
		4: { name: 'Ready for Inspection', color: '#9e9e9e'  },
		5: { name: 'Inspection',           color: '#6a0dad'  },
		6: { name: 'Clean Ready',          color: '#2e7d32'  },
		7: { name: 'Checked-In',           color: '#1565c0'  },
		8: { name: 'In Progress',          color: '#0288d1'  },
		9: { name: 'Not Available',        color: '#c62828'  },
	};

	constructor(private http: HttpClient) { }

	/**
	 * Load board rows for a given date and view type.
	 * @param date     YYYY-MM-DD (defaults to today HST on server if omitted)
	 * @param type     'arrivals' | 'departures' | 'all'
	 */
	getBoard(date?: string, type: 'arrivals' | 'departures' | 'all' = 'arrivals'): Observable<any[]> {
		let params = new HttpParams().set('type', type);
		if (date) { params = params.set('date', date); }
		return this.http.get<any[]>(`${this.base}/today`, { params })
			.pipe(retry({ count: 2, delay: 1500 }));
	}

	preview(reservationId: number, stageCode: number): Observable<any> {
		return this.http.get<any>(`${this.base}/${reservationId}/preview/${stageCode}`)
			.pipe(retry({ count: 1, delay: 1000 }));
	}

	send(reservationId: number, stageCode: number, createdBy?: string): Observable<any> {
		let params = new HttpParams();
		if (createdBy) { params = params.append('createdBy', createdBy); }
		return this.http.post<any>(`${this.base}/${reservationId}/send/${stageCode}`, null, { params });
	}

	/**
	 * Re-run the backfill logic for a single unit right now.
	 * Maps current occupancyStatus → ArrivalStageEnum → trySend() for today's arrival.
	 * Returns { shortTermRentalId, propertyCode, currentStatus, arrivals, sent[], skipped[] }.
	 */
	refresh(shortTermRentalId: number): Observable<any> {
		return this.http.post<any>(`${this.base}/refresh/${shortTermRentalId}`, null);
	}

	getAccessCodes(reservationId: number): Observable<any> {
		return this.http.get<any>(`${environment.apiUrl}/access-codes`, {
			params: new HttpParams().set('reservationId', String(reservationId))
		});
	}

	createAccessCode(dto: any): Observable<any> {
		return this.http.post<any>(`${environment.apiUrl}/access-codes`, dto);
	}

	updateAccessCode(dto: any): Observable<any> {
		return this.http.patch<any>(`${environment.apiUrl}/access-codes`, dto);
	}

	/** Manual room-status override — PATCH /arrivals/{strId}/room-status */
	updateRoomStatus(shortTermRentalId: number, occupancyStatus: number): Observable<any> {
		return this.http.patch<any>(`${this.base}/${shortTermRentalId}/room-status`, { occupancyStatus });
	}
}
